import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Minus, Target, TrendingDown, TrendingUp, Trophy, Users } from 'lucide-react';
import { CURRENT_MONTH, getMonthSummary, getOfficerTrend, getRosterForMonth, MONTH_KEYS, MONTH_OPTIONS, RosterEntry } from '../data/adminRoster';
import { OfficerScorecardModal } from './OfficerScorecardModal';
type SortKey = 'points' | 'name' | 'unitsClosed' | 'callAnswerRate';
const RANK_STYLES: Record<string, string> = {
  'LOAN PARTNER': 'bg-teal-50 text-teal-700 border-teal-200',
  'LOAN OFFICER': 'bg-blue-50 text-blue-700 border-blue-200',
  'SENIOR LOAN OFFICER': 'bg-purple-50 text-purple-700 border-purple-200',
  'EXECUTIVE LOAN OFFICER': 'bg-amber-50 text-amber-700 border-amber-200'
};
export function AdminView() {
  const [monthKey, setMonthKey] = useState(CURRENT_MONTH);
  const [sortKey, setSortKey] = useState<SortKey>('points');
  const [selectedOfficer, setSelectedOfficer] = useState<string | null>(null);
  const monthIndex = MONTH_KEYS.indexOf(monthKey);
  const monthLabel = MONTH_OPTIONS.find(option => option.key === monthKey)?.label ?? monthKey;
  const isCurrentMonth = monthKey === CURRENT_MONTH;
  const roster = useMemo(() => getRosterForMonth(monthKey), [monthKey]);
  const summary = useMemo(() => getMonthSummary(monthKey), [monthKey]);
  const sortedRoster = useMemo(() => {
    const entries = [...roster];
    if (sortKey === 'name') {
      return entries.sort((a, b) => a.name.localeCompare(b.name));
    }
    return entries.sort((a, b) => b[sortKey] - a[sortKey]);
  }, [roster, sortKey]);

  // Older months are earlier in the array, so "previous" walks the index up.
  const goToPreviousMonth = () => {
    const next = MONTH_KEYS[monthIndex + 1];
    if (next) setMonthKey(next);
  };
  const goToNextMonth = () => {
    const next = MONTH_KEYS[monthIndex - 1];
    if (next) setMonthKey(next);
  };
  return <div className="space-y-6" data-id="element-38" data-mp="1xs9r1rvmxg76">
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" aria-label="Team points overview" data-id="element-39" data-mp="1xvnt0z0dzne3">
        <div className="flex flex-col gap-4 bg-[#0a1e5e] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-40" data-mp="135awgxika0r4">
          <div data-id="element-41" data-mp="1i33crrvxmt6d">
            <div className="flex items-center gap-2" data-id="element-42" data-mp="1i1iqepjrugjl">
              <h1 className="text-2xl font-extrabold tracking-tight text-white" data-id="element-43" data-mp="1d5jxconi0acu">
                Team Points Overview
              </h1>
              <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-100" data-id="element-44" data-mp="pf7jkx5knjec">
                Admin
              </span>
            </div>
            <p className="mt-1 text-sm font-medium text-blue-200" data-id="element-45" data-mp="z623huvci6wm">
              All Loan Officers and their point totals for {monthLabel}.
            </p>
          </div>

          <div className="flex items-center gap-2" data-id="element-46" data-mp="6nengtv7hi2e">
            <button type="button" onClick={goToPreviousMonth} disabled={monthIndex >= MONTH_KEYS.length - 1} className="rounded-lg border border-white/20 bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white disabled:cursor-not-allowed disabled:opacity-40" aria-label="Previous month" data-id="element-47" data-mp="6m3kuxdhygcv">
              <ChevronLeft className="h-4 w-4" data-id="element-48" data-mp="28d2on8q3kcnr" />
            </button>
            <label className="sr-only" htmlFor="admin-month" data-id="element-49" data-mp="28b9cxf0mn28w">
              Select month
            </label>
            <select id="admin-month" value={monthKey} onChange={event => setMonthKey(event.target.value)} className="rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-white [&>option]:text-gray-900" data-id="element-50" data-mp="3129ivviiqg37">
              {MONTH_OPTIONS.map(option => <option key={option.key} value={option.key} data-id="element-51" data-mp="1foyh0hnytpif">
                  {option.label}
                  {option.isCurrent ? ' (current)' : ''}
                </option>)}
            </select>
            <button type="button" onClick={goToNextMonth} disabled={monthIndex <= 0} className="rounded-lg border border-white/20 bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white disabled:cursor-not-allowed disabled:opacity-40" aria-label="Next month" data-id="element-52" data-mp="1r5mhvqjwmy24">
              <ChevronRight className="h-4 w-4" data-id="element-53" data-mp="29vcemj789two" />
            </button>
          </div>
        </div>

        {!isCurrentMonth && <div className="border-b border-amber-100 bg-amber-50 px-5 py-2.5 text-xs font-semibold text-amber-900 sm:px-6" data-id="element-54" data-mp="24vg92fivtuf0">
            Viewing historical results for {monthLabel}. Totals are final for
            closed months.
          </div>}

        <div className="grid grid-cols-2 gap-4 p-5 sm:px-6 lg:grid-cols-4" data-id="element-55" data-mp="pg0ph3tf7l1r">
          <SummaryStat icon={<Trophy className="h-4 w-4 text-blue-600" data-id="element-57" data-mp="313mcy0mm22d3" />} label="Total team points" value={summary.totalPoints.toLocaleString()} delta={summary.totalPointsDelta} data-id="element-56" data-mp="pi3qeiw42yl9" />
          <SummaryStat icon={<Target className="h-4 w-4 text-green-600" data-id="element-59" data-mp="3a394zvtg9vth" />} label="At or above goal" value={`${summary.qualifiedCount} of ${summary.officerCount}`} data-id="element-58" data-mp="3a3it1duxpswj" />
          <SummaryStat icon={<Users className="h-4 w-4 text-purple-600" data-id="element-61" data-mp="3jtc8wr240u6l" />} label="Average points" value={summary.averagePoints.toLocaleString()} data-id="element-60" data-mp="3junbwonqdxwn" />
          <SummaryStat icon={<TrendingUp className="h-4 w-4 text-teal-600" data-id="element-63" data-mp="3sv1uhg9b8ywg" />} label="Units closed" value={summary.totalUnits.toLocaleString()} data-id="element-62" data-mp="3suaal2p2spm0" />
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" aria-label="Loan officer point totals" data-id="element-64" data-mp="1yhst6bqx4jpa">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-65" data-mp="36q84fbsrtqgw">
          <div data-id="element-66" data-mp="31sq60fcdqtn5">
            <h2 className="text-sm font-bold uppercase tracking-wider text-blue-600" data-id="element-67" data-mp="31t9ez6k1y7p1">
              Loan Officer Roster
            </h2>
            <p className="mt-0.5 text-xs text-gray-500" data-id="element-68" data-mp="3ascy2aj7yn3j">
              {summary.officerCount} users · sorted by{' '}
              {sortKey === 'name' ? 'name' : sortKey === 'points' ? 'points' : sortKey === 'unitsClosed' ? 'units closed' : 'answer rate'}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500" data-id="element-69" data-mp="3ug2g3q1ct8zk">
            <span data-id="element-70" data-mp="3uigdw6nf4zat">Sort</span>
            <select value={sortKey} onChange={event => setSortKey(event.target.value as SortKey)} className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-bold text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Sort roster by" data-id="element-71" data-mp="c9xydweq42fb">
              <option value="points" data-id="element-72" data-mp="gz0byrt9dns8">Points</option>
              <option value="unitsClosed" data-id="element-73" data-mp="gy739laqoo7g">Units closed</option>
              <option value="callAnswerRate" data-id="element-74" data-mp="lxk0mzz8ldoa">Answer rate</option>
              <option value="name" data-id="element-75" data-mp="m066mv6hbl4e">Name</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-76" data-mp="84lq26e83f05">
          <table className="w-full min-w-[880px] border-collapse text-left" data-id="element-77" data-mp="cxwuk0vkbjow">
            <thead data-id="element-78" data-mp="3udwlfsqapf0w">
              <tr className="border-b border-gray-200 bg-gray-50" data-id="element-79" data-mp="3ueo8ug9qo6g3">
                <th scope="col" className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-80" data-mp="3uc24sqm2492m">
                  #
                </th>
                <th scope="col" className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-81" data-mp="3pek2vk6giu44">
                  Loan Officer
                </th>
                <th scope="col" className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-82" data-mp="4pkeo8us3osb">
                  Points
                </th>
                <th scope="col" className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-83" data-mp="3qw8zdmonyt6m">
                  Goal progress
                </th>
                <th scope="col" className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-84" data-mp="3i2nh3tdaitsg">
                  vs prior month
                </th>
                <th scope="col" className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-85" data-mp="3377v8bmv6475">
                  Units
                </th>
                <th scope="col" className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-86" data-mp="1emsamy4oid89">
                  Answer rate
                </th>
                <th scope="col" className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-87" data-mp="1npb8t4xis1o6">
                  Dials
                </th>
                <th scope="col" className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-88" data-mp="zyxflb377ws0">
                  12-month trend
                </th>
                <th scope="col" className="px-4 py-3" data-id="element-89" data-mp="19p8xk1jpw6hw">
                  <span className="sr-only" data-id="element-90" data-mp="19qtqjmqdvhdn">View scorecard</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-91" data-mp="h8g0i9bagzp4">
              {sortedRoster.map((entry, index) => <RosterRow key={entry.name} entry={entry} monthLabel={monthLabel} onSelect={() => setSelectedOfficer(entry.name)} position={sortKey === 'points' ? index + 1 : roster.findIndex(item => item.name === entry.name) + 1} data-id="element-92" data-mp="1i329r8k2hbvp" data-mpi={index} />)}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-t border-gray-200 bg-gray-50 px-5 py-3 text-[11px] font-semibold text-gray-500 sm:px-6" data-id="element-93" data-mp="14b5o9ncwm08a">
          <span className="flex items-center gap-1.5" data-id="element-94" data-mp="198xhckpabse2">
            <span className="h-2 w-2 rounded-full bg-green-500" data-id="element-95" data-mp="1e1qk53170qni" />
            At or above monthly goal
          </span>
          <span className="flex items-center gap-1.5" data-id="element-96" data-mp="gkrunseo7tbl">
            <span className="h-2 w-2 rounded-full bg-amber-400" data-id="element-97" data-mp="lkxptik5fxyf" />
            Within 20% of goal
          </span>
          <span className="flex items-center gap-1.5" data-id="element-98" data-mp="2yuxf8s7cvpo0">
            <span className="h-2 w-2 rounded-full bg-red-400" data-id="element-99" data-mp="2yueakd6puxrx" />
            Below 80% of goal
          </span>
          <span className="ml-auto text-gray-400" data-id="element-100" data-mp="2urhlc3mo83f0">
            Select a row for the full scorecard
          </span>
        </div>
      </section>

      <OfficerScorecardModal officerName={selectedOfficer} monthKey={monthKey} onClose={() => setSelectedOfficer(null)} data-id="element-101" data-mp="2kvxv9xusfyy6" />
    </div>;
}
function SummaryStat({
  icon,
  label,
  value,
  delta
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  delta?: number | null;
}) {
  return <div className="rounded-lg border border-gray-200 bg-white p-4" data-id="element-102" data-mp="274t13ba6vyv8">
      <div className="flex items-center gap-1.5" data-id="element-103" data-mp="225gcom3o6jqj">
        {icon}
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-104" data-mp="2233ohr7bjd51">
          {label}
        </p>
      </div>
      <p className="mt-1.5 text-2xl font-extrabold text-[#0a1e5e]" data-id="element-105" data-mp="1s68pz8b1pcqr">{value}</p>
      {delta !== undefined && delta !== null && <div className="mt-1" data-id="element-106" data-mp="1iid7v9z7bapt">
          <DeltaBadge delta={delta} data-id="element-107" data-mp="1il7th4tyb1kt" />
        </div>}
    </div>;
}
function DeltaBadge({
  delta
}: {
  delta: number;
}) {
  if (delta === 0) {
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-gray-400" data-id="element-108" data-mp="19jhxrmt1dgh8">
        <Minus className="h-3.5 w-3.5" data-id="element-109" data-mp="1dja3lrr1p8iw" />
        No change
      </span>;
  }
  const positive = delta > 0;
  return <span className={`inline-flex items-center gap-1 text-xs font-bold ${positive ? 'text-green-600' : 'text-red-600'}`} data-id="element-110" data-mp="21axnvqtsxlc7">
      {positive ? <TrendingUp className="h-3.5 w-3.5" data-id="element-111" data-mp="1shkhpn6q8n86" /> : <TrendingDown className="h-3.5 w-3.5" data-id="element-112" data-mp="1sh2g0b1apgsv" />}
      {positive ? '+' : ''}
      {delta.toLocaleString()}
    </span>;
}
function RosterRow({
  entry,
  position,
  monthLabel,
  onSelect
}: {
  entry: RosterEntry;
  position: number;
  monthLabel: string;
  onSelect: () => void;
}) {
  const trend = getOfficerTrend(entry.name);
  const goalTone = entry.qualified ? 'bg-green-500' : entry.goalPercent >= 80 ? 'bg-amber-400' : 'bg-red-400';
  return <tr onClick={onSelect} onKeyDown={event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  }} tabIndex={0} aria-label={`View ${entry.name} scorecard for ${monthLabel}`} className="group cursor-pointer transition-colors hover:bg-blue-50/60 focus:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500" data-id="element-113" data-mp="8kdhvebcqwlp">
      <td className="px-4 py-3 text-sm font-extrabold text-gray-400" data-id="element-114" data-mp="4jtjy8iyfoyi">
        {position}
      </td>
      <td className="px-4 py-3" data-id="element-115" data-mp="3vpl7d05cknhm">
        <p className="text-sm font-bold text-gray-800" data-id="element-116" data-mp="3vo9u89q0i3du">{entry.name}</p>
        <span className={`mt-1 inline-block rounded-full border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${RANK_STYLES[entry.rank] ?? 'border-gray-200 bg-gray-50 text-gray-600'}`} data-id="element-117" data-mp="1fxx991elkox8">
          {entry.rank}
        </span>
      </td>
      <td className="px-4 py-3 text-right" data-id="element-118" data-mp="3lmzeoznbsqre">
        <p className={`text-lg font-extrabold ${entry.qualified ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-119" data-mp="3lnsaj5kqxvur">
          {entry.points.toLocaleString()}
        </p>
        <p className="text-[10px] font-semibold text-gray-400" data-id="element-120" data-mp="3unxiphxrky9z">
          goal {entry.goal.toLocaleString()}
        </p>
      </td>
      <td className="px-4 py-3" data-id="element-121" data-mp="3guqqoa3qnyp">
        <div className="flex items-center gap-2" data-id="element-122" data-mp="8gh5511o2a93">
          <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-100" data-id="element-123" data-mp="8f60z04b70hg">
            <div className={`h-full rounded-full ${goalTone}`} style={{
            width: `${Math.min(100, entry.goalPercent)}%`
          }} data-id="element-124" data-mp="d6xhehtr3ptv" />
          </div>
          <span className="text-xs font-bold text-gray-600" data-id="element-125" data-mp="m841t1zitkse">
            {entry.goalPercent}%
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-right" data-id="element-126" data-mp="r80672c4re8h">
        {entry.pointsDelta === null ? <span className="text-xs font-semibold text-gray-400" data-id="element-127" data-mp="11b5gprginfj2">—</span> : <DeltaBadge delta={entry.pointsDelta} data-id="element-128" data-mp="wb9dvvjk1u53" />}
      </td>
      <td className="px-4 py-3 text-right text-sm font-bold text-gray-700" data-id="element-129" data-mp="we5bfirhqgt0">
        {entry.unitsClosed}
      </td>
      <td className="px-4 py-3 text-right" data-id="element-130" data-mp="nb481pu0f3t1">
        <span className={`text-sm font-bold ${entry.callAnswerRate >= 90 ? 'text-green-600' : entry.callAnswerRate >= 80 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-131" data-mp="ncfk0cxlzhv8">
          {entry.callAnswerRate}%
        </span>
      </td>
      <td className="px-4 py-3 text-right text-sm font-bold text-gray-700" data-id="element-132" data-mp="1f4mpqibkt9uu">
        {entry.dials.toLocaleString()}
      </td>
      <td className="px-4 py-3" data-id="element-133" data-mp="16ai3ydbauom6">
        <TrendSparkline trend={trend} data-id="element-134" data-mp="16971qhxi5ox6" />
      </td>
      <td className="px-4 py-3 text-right" data-id="element-135" data-mp="22ruv10908iwv">
        <ChevronRight className="ml-auto h-4 w-4 text-gray-300 transition-colors group-hover:text-[#0a1e5e]" data-id="element-136" data-mp="22so5ocb33ska" />
      </td>
    </tr>;
}
function TrendSparkline({
  trend
}: {
  trend: {
    monthKey: string;
    label: string;
    points: number;
  }[];
}) {
  const max = Math.max(...trend.map(point => point.points), 1);
  return <div className="flex h-8 items-end gap-0.5" role="img" aria-label={`12 month point trend, ending at ${trend[trend.length - 1]?.points.toLocaleString()} points`} data-id="element-137" data-mp="11xdzqms8xjj">
      {trend.map(point => <span key={point.monthKey} title={`${point.label}: ${point.points.toLocaleString()}`} className="w-1.5 rounded-sm bg-blue-200" style={{
      height: `${Math.max(8, point.points / max * 100)}%`
    }} data-id="element-138" data-mp="3nfxa25i73mbg" />)}
    </div>;
}